import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import clientService from "@/services/client";
import { Client } from "@/types/client";
import { ArrowRight, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";

interface ClientOption {
  id: string;
  label: string;
}

function ClientPicker({
  placeholder,
  value,
  onChange,
  excludeId,
}: {
  placeholder: string;
  value: ClientOption | null;
  onChange: (option: ClientOption | null) => void;
  /** Não deixa selecionar o mesmo contato já escolhido no outro campo. */
  excludeId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      const [res] = await clientService.getAll(query, 1, 20);
      if (!active) return;
      setOptions(
        (res?.result || [])
          .filter((c) => c._id !== excludeId)
          .map((c) => ({
            id: c._id,
            label: c.phone || c.email ? `${c.name} · ${c.phone || c.email}` : c.name,
          }))
      );
      setLoading(false);
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, open, excludeId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className="truncate">{value?.label || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar contato..." value={query} onValueChange={setQuery} />
          <CommandList>
            {loading && <div className="py-4 text-center text-sm text-muted-foreground">Buscando...</div>}
            {!loading && <CommandEmpty>Nenhum contato encontrado</CommandEmpty>}
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.id}
                  onSelect={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface MergeClientsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Pré-seleciona o contato que permanece (ex.: mesclar a partir do menu de ações de uma linha). */
  initialSurvivor?: Client;
}

export default function MergeClientsDialog({
  open,
  onClose,
  onSuccess,
  initialSurvivor,
}: MergeClientsDialogProps) {
  const { toast } = useToast();
  const [survivor, setSurvivor] = useState<ClientOption | null>(null);
  const [merged, setMerged] = useState<ClientOption | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSurvivor(initialSurvivor ? { id: initialSurvivor._id, label: initialSurvivor.name } : null);
    setMerged(null);
  }, [open, initialSurvivor]);

  const handleMerge = async () => {
    if (!survivor || !merged) return;
    setLoading(true);
    const [, error] = await clientService.merge(survivor.id, merged.id);
    setLoading(false);
    if (error) {
      toast({
        title: "Erro",
        description: error.msg || "Não foi possível mesclar os contatos",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Contatos mesclados",
      description: `${merged.label} foi combinado em ${survivor.label} e removido.`,
    });
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Mesclar contatos</DialogTitle>
          <DialogDescription>
            Combina empresas, projetos, tags, e-mails e telefones dos dois contatos num só (nenhum
            dado é perdido, mesmo quando os dois têm valores diferentes). Tickets, conversas,
            chamadas e notificações do contato mesclado passam a pertencer ao que permanece — o
            outro é apagado. Essa ação não pode ser desfeita.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <label className="text-sm font-medium">Contato que permanece</label>
            <ClientPicker
              placeholder="Selecione o contato"
              value={survivor}
              onChange={setSurvivor}
              excludeId={merged?.id}
            />
          </div>
          <div className="flex items-center justify-center text-muted-foreground">
            <ArrowRight className="h-4 w-4" />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Contato que será mesclado (e apagado)</label>
            <ClientPicker
              placeholder="Selecione o contato"
              value={merged}
              onChange={setMerged}
              excludeId={survivor?.id}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleMerge}
            disabled={!survivor || !merged || loading}
          >
            {loading ? "Mesclando..." : "Mesclar contatos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
